import { useLLMSelection } from "@/features/llm-selection";
import { useToast } from "@/features/toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { ModuleId } from "@/modules";
import type { ChatAttachment } from "../types";
import { createDraftChatKey, draftRoutePath } from "../store/streaming-store";

export interface FirstMessageOptions {
  /**
   * false → sayfaya gidilmez; çağıran taraf (ör. modül dock'u) stream'i
   * kendi yüzeyinde başlatır. Varsayılan true (landing akışı).
   */
  navigateToChat?: boolean;
  /** Dock'tan açılıyorsa modül. Stream gövdesine scope üzerinden gider. */
  module?: ModuleId;
}

interface FirstMessageResult {
  success: boolean;
  isFirstMessage?: boolean;
  error?: Error;
}

/**
 * İlk mesajı sohbet sayfasına taşır.
 *
 * Konuşma burada açılmaz. Stream `chatId` olmadan gider; id ancak
 * `conversation_created` ile gelir. Sağlayıcı hata verirse satır kalmaz.
 */
export const useFirstMessageHandler = () => {
  const { t } = useTranslation();
  const { error: showError } = useToast();
  const navigate = useNavigate();
  const { selectedModel } = useLLMSelection();

  const handleFirstMessage = async (
    message: string,
    attachments?: ChatAttachment[],
    options: FirstMessageOptions = {},
  ): Promise<FirstMessageResult> => {
    const { navigateToChat = true } = options;
    try {
      if (!selectedModel) {
        throw new Error(t("error.noModelSelected"));
      }

      if (navigateToChat) {
        navigate(draftRoutePath(createDraftChatKey()), {
          state: {
            pendingMessage: message,
            pendingAttachments: attachments,
          },
        });
      }

      return {
        success: true,
        isFirstMessage: true,
      };
    } catch (error) {
      console.error("❌ First message handler error:", error);

      const errorMessage =
        error instanceof Error
          ? error.message
          : t("error.conversationCreationFailed");

      showError(errorMessage, {
        title: t("error.title"),
        actions: [
          {
            label: t("common.retry"),
            action: () => handleFirstMessage(message, attachments, options),
          },
        ],
      });

      return {
        success: false,
        error: error instanceof Error ? error : new Error(errorMessage),
      };
    }
  };

  return {
    handleFirstMessage,
    isCreating: false,
  };
};
